import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE, NAV } from "@/data/content";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-cream/80 backdrop-blur-xl border-b border-stone-200/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={SITE.logo} alt="Sober Life Foundation" className="h-12 w-12 object-contain" />
          <span className="hidden sm:block leading-none">
            <span className="block font-heading text-xl text-maroon-deep tracking-tight">Sober Life Foundation</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-teal">Ulhasnagar</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm tracking-wide transition-colors duration-300 relative py-1 ${
                pathname === n.to ? "text-maroon" : "text-stone-600 hover:text-maroon"
              }`}
            >
              {n.label}
              {pathname === n.to && (
                <span className="absolute left-0 -bottom-0.5 h-px w-full bg-sunrise" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneHref}`}
            data-testid="navbar-help-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-sunrise text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-ember transition-colors duration-300 shadow-lg shadow-orange-500/20"
          >
            <Phone size={15} /> Get Help
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-maroon-deep p-1"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-cream/95 backdrop-blur-xl border-t border-stone-200 px-6 py-6 space-y-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block font-heading text-2xl text-maroon-deep py-2"
            >
              {n.label}
            </Link>
          ))}
          <a
            href={`tel:${SITE.phoneHref}`}
            className="mt-4 inline-flex items-center gap-2 bg-sunrise text-white px-6 py-3 rounded-full text-sm font-medium"
          >
            <Phone size={15} /> Get Help Now
          </a>
        </div>
      )}
    </header>
  );
};
